import type { CSSProperties } from 'react';
import type { WeeklyLearningGuideExportPayload } from './weeklyLearningGuideExcelExport';

const A4_HEIGHT_MM = 297;
const SHEET_PADDING_MM = 8;
const TITLE_LINE1_MM = 14;
const TITLE_LINE2_MM = 6;
const GRID_GAP_MM = 4;
const GRID_WEEK_BLOCK_PADDING_MM = 2;
const GRID_WEEK_TITLE_MM = 5;
const NEIS_GRID_SHEET_PADDING_MM = 6;
const NEIS_PAGE_LEGEND_MM = 7;
export const NEIS_GRID_ROWS = 4;
export const NEIS_GRID_COLS = 2;
export const NEIS_GRID_WEEKS_PER_PAGE = NEIS_GRID_ROWS * NEIS_GRID_COLS;

export type WeeklyGuideA4LayoutOptions = {
  /** NEIS 학기 export: A4 2열×4행 그리드 안 한 주 블록 */
  gridCell?: boolean;
  /** NEIS 학기 export: 페이지 상단 범례 높이 반영 */
  gridPageWithLegend?: boolean;
};

function clamp(min: number, value: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/** A4 세로 1페이지(또는 2열×4행 그리드 셀)에 맞춘 표·글자 크기 CSS 변수 */
export function computeWeeklyGuideA4LayoutStyle(
  payload: WeeklyLearningGuideExportPayload,
  options?: WeeklyGuideA4LayoutOptions
): CSSProperties {
  const gridCell = options?.gridCell ?? false;
  const gridPageWithLegend = options?.gridPageWithLegend ?? false;
  const hasSecondTitle = payload.titleLine2.trim().length > 0;
  const titleBlockMm = TITLE_LINE1_MM + (hasSecondTitle ? TITLE_LINE2_MM : 0);
  const neisPageLegendMm = gridCell && gridPageWithLegend ? NEIS_PAGE_LEGEND_MM : 0;
  const availableTableMm = gridCell
    ? (A4_HEIGHT_MM -
        NEIS_GRID_SHEET_PADDING_MM * 2 -
        neisPageLegendMm -
        GRID_GAP_MM * (NEIS_GRID_ROWS - 1) -
        GRID_WEEK_BLOCK_PADDING_MM * (NEIS_GRID_ROWS - 1)) /
        NEIS_GRID_ROWS -
      GRID_WEEK_TITLE_MM -
      0.5
    : A4_HEIGHT_MM - SHEET_PADDING_MM * 2 - titleBlockMm;

  const periodCount = Math.max(payload.periods.length, 1);
  const rowUnitsPerPeriod = payload.neisMode ? 1 : 7;
  const noticeRowUnits = gridCell || payload.neisMode ? 0 : rowUnitsPerPeriod;
  const totalRowUnits = 2 + periodCount * rowUnitsPerPeriod + noticeRowUnits;
  const unitHeightMm = availableTableMm / totalRowUnits;
  const noticeRowHeightMm = unitHeightMm * rowUnitsPerPeriod;

  const cellFontPt = clamp(4.5, unitHeightMm * 0.58, gridCell ? 7.5 : 12);
  const subjectFontPt = clamp(5, unitHeightMm * 0.64, gridCell ? 8 : 13);
  const periodFontPt = clamp(4.5, unitHeightMm * 0.48, gridCell ? 7 : 10.5);
  const titleFontPt = clamp(15, A4_HEIGHT_MM * 0.058, 22);
  const title2FontPt = clamp(12, titleFontPt * 0.78, 17);
  const noticeFontPt = clamp(7, unitHeightMm * 0.52, 11.5);
  const gridTitleFontPt = clamp(6.5, unitHeightMm * 0.62, 10);

  return {
    '--wlg-a4-title-font': `${titleFontPt}pt`,
    '--wlg-a4-title2-font': `${title2FontPt}pt`,
    '--wlg-a4-cell-font': `${cellFontPt}pt`,
    '--wlg-a4-subject-font': `${subjectFontPt}pt`,
    '--wlg-a4-period-font': `${periodFontPt}pt`,
    '--wlg-a4-notice-font': `${noticeFontPt}pt`,
    '--wlg-a4-grid-title-font': `${gridTitleFontPt}pt`,
    '--wlg-a4-row-1line-h': `${unitHeightMm}mm`,
    '--wlg-a4-row-learning-h': `${unitHeightMm * 3}mm`,
    '--wlg-a4-notice-row-h': `${noticeRowHeightMm}mm`,
  } as CSSProperties;
}

/** NEIS 학기 export: 주차 블록을 A4 8주(2열×4행) 단위로 묶음 */
export function chunkWeeklyGuideTermWeekBlocks<T>(
  blocks: T[],
  perPage = NEIS_GRID_WEEKS_PER_PAGE
): T[][] {
  const pages: T[][] = [];
  for (let i = 0; i < blocks.length; i += perPage) {
    pages.push(blocks.slice(i, i + perPage));
  }
  return pages;
}
